import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { TrendingUp, History } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Prior Assessment History tile.
 *
 * Fetches the merged prior + MOSAIC growth timeline for a student and renders
 * a dual-axis growth chart plus a tabular history. Renders nothing if the
 * student has no prior scores on file — the current MOSAIC result page is
 * unaffected for students without a prior-year record.
 *
 * Props:
 *   studentId  — MOSAIC user_id of the student (required)
 *   compact    — bool, defaults false; when true hides the row-level table
 */
const PriorAssessmentHistory = ({ studentId, compact = false }) => {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/prior-scores/student/${studentId}/growth`);
        if (!cancelled) setTimeline(data.timeline || []);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [studentId]);

  if (loading) return null;
  if (err) return null; // silent — tile is optional

  const priorEvents = timeline.filter((t) => t.kind === "prior");
  if (priorEvents.length === 0) return null; // no prior scores → nothing to show

  // Chart data: one row per event, with mosaic_wida and nyseslat_raw as separate series
  const chartData = timeline.map((ev, i) => {
    const label = ev.year || (ev.test_date ? ev.test_date.slice(0, 10) : `Event ${i + 1}`);
    const row = { label };
    if (ev.kind === "mosaic") row.mosaic_wida = ev.overall;
    if (ev.kind === "prior" && ev.scale === "nyseslat_1_5") row.nyseslat_raw = ev.overall;
    if (ev.kind === "prior" && ev.scale === "wida_1_6") row.wida_access = ev.overall;
    return row;
  });

  const hasNYSESLAT = priorEvents.some((e) => e.scale === "nyseslat_1_5");
  const hasWIDAACCESS = priorEvents.some((e) => e.scale === "wida_1_6");

  return (
    <div
      className="border-2 border-gray-200 mb-8"
      data-testid="prior-assessment-history-tile"
    >
      <div className="p-6 border-b border-gray-200 flex items-center gap-3">
        <History size={22} className="text-[#002FA7]" />
        <div>
          <h3
            className="text-xl font-bold"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Prior Assessment History
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Growth trajectory combining prior-year language proficiency results with current MOSAIC performance
          </p>
        </div>
      </div>

      {/* Chart */}
      <div className="p-6" data-testid="prior-growth-chart">
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              {hasNYSESLAT && (
                <YAxis
                  yAxisId="nyseslat"
                  orientation="left"
                  domain={[1, 5]}
                  ticks={[1, 2, 3, 4, 5]}
                  label={{
                    value: "NYSESLAT (1-5)",
                    angle: -90,
                    position: "insideLeft",
                    fontSize: 11,
                    fill: "#6b7280",
                  }}
                  tick={{ fontSize: 11 }}
                />
              )}
              <YAxis
                yAxisId="mosaic"
                orientation="right"
                domain={[1, 6]}
                ticks={[1, 2, 3, 4, 5, 6]}
                label={{
                  value: "MOSAIC / WIDA (1-6)",
                  angle: 90,
                  position: "insideRight",
                  fontSize: 11,
                  fill: "#6b7280",
                }}
                tick={{ fontSize: 11 }}
              />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 6 }}
                formatter={(v, name) => [v, name]}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {hasNYSESLAT && (
                <Line
                  yAxisId="nyseslat"
                  type="monotone"
                  dataKey="nyseslat_raw"
                  name="NYSESLAT (prior)"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={{ r: 5 }}
                  connectNulls
                />
              )}
              {hasWIDAACCESS && (
                <Line
                  yAxisId="mosaic"
                  type="monotone"
                  dataKey="wida_access"
                  name="WIDA ACCESS (prior)"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  dot={{ r: 5 }}
                  connectNulls
                />
              )}
              <Line
                yAxisId="mosaic"
                type="monotone"
                dataKey="mosaic_wida"
                name="MOSAIC (current)"
                stroke="#002FA7"
                strokeWidth={2.5}
                dot={{ r: 6 }}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        {hasNYSESLAT && (
          <p className="text-[11px] text-gray-500 mt-3 italic">
            NYSESLAT is reported on a 1-5 scale; MOSAIC and WIDA ACCESS use a 1-6 scale. Series are shown on their native axes without numeric conversion.
          </p>
        )}
      </div>

      {/* Row table */}
      {!compact && (
        <div className="border-t border-gray-200 overflow-x-auto" data-testid="prior-score-rows">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-6 py-3 font-semibold">Year</th>
                <th className="px-6 py-3 font-semibold">Assessment</th>
                <th className="px-6 py-3 font-semibold">Overall</th>
                <th className="px-6 py-3 font-semibold">Level</th>
                <th className="px-6 py-3 font-semibold text-center">L</th>
                <th className="px-6 py-3 font-semibold text-center">S</th>
                <th className="px-6 py-3 font-semibold text-center">R</th>
                <th className="px-6 py-3 font-semibold text-center">W</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {priorEvents.map((ev, i) => (
                <tr key={i} data-testid={`prior-row-${i}`}>
                  <td className="px-6 py-3">{ev.year || "—"}</td>
                  <td className="px-6 py-3 font-medium">{ev.assessment}</td>
                  <td className="px-6 py-3">{ev.overall ?? "—"}</td>
                  <td className="px-6 py-3">{ev.overall_label || "—"}</td>
                  <td className="px-6 py-3 text-center">{ev.domain_scores?.listening ?? "—"}</td>
                  <td className="px-6 py-3 text-center">{ev.domain_scores?.speaking ?? "—"}</td>
                  <td className="px-6 py-3 text-center">{ev.domain_scores?.reading ?? "—"}</td>
                  <td className="px-6 py-3 text-center">{ev.domain_scores?.writing ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PriorAssessmentHistory;
